/**
 * GlossyButton Component
 *
 * Purpose: Reusable button with glossiness.jpg background effect.
 * Used consistently across the application for primary actions.
 *
 * Features:
 * - Glossy background with 0.8 opacity
 * - Dark text for readability
 * - Loading state with spinner
 * - Smooth hover scale animation
 * - Disabled state handling
 */

import { animated, useSpring } from "@react-spring/web";
import type React from "react";
import { useState } from "react";

interface GlossyButtonProps {
  label: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
}

export const GlossyButton: React.FC<GlossyButtonProps> = ({
  label,
  onClick,
  loading = false,
  disabled = false,
  className = "",
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Smooth hover animation
  const hoverSpring = useSpring({
    transform: isHovered && !disabled && !loading ? "scale(1.05)" : "scale(1)",
    boxShadow:
      isHovered && !disabled && !loading
        ? "0 10px 25px -5px rgba(0, 0, 0, 0.3)"
        : "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    config: { tension: 300, friction: 20 },
  });

  return (
    <animated.button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        ...hoverSpring,
        backgroundImage:
          isHovered && !disabled && !loading
            ? "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(245,245,255,0.95) 100%), url(/glossiness.jpg)"
            : "url(/glossiness.jpg)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundBlendMode: isHovered ? "overlay" : "normal",
      }}
      className={`
        px-6 py-3 rounded-xl font-semibold
        transition-all duration-300
        disabled:opacity-50 disabled:cursor-not-allowed
        border-2 border-gray-200/50
        ${
          isHovered && !disabled && !loading
            ? "text-gray-900 bg-white/90"
            : "text-gray-700 bg-white/70"
        }
        backdrop-blur-sm
        ${className}
      `}
    >
      {loading && (
        <div className="flex items-center space-x-2">
          <svg
            className="animate-spin h-5 w-5 text-gray-800"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <title>Loading</title>
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span>{label}</span>
        </div>
      )}
      {!loading && <span>{label}</span>}
    </animated.button>
  );
};
